// "Invitation ready": port of InviteSentView + InviteCodeCard (GroundTeam/Views/Sync/InviteMemberSheet.swift). Who
// was invited, the code as XXXX-XXXX-XXXX with its expiry, and the ways to pass it on: Copy code, Copy message (the
// iPhone's share text plus the console link), Copy link (/welcome#code=…) and Email (mailto: with the message, since
// the server never emails invitations). Shown after an invite and when an admin opens a pending invitation.

import { BadgeCheck, Check, Clock, Copy, Link2, Mail, MessageSquareText, TriangleAlert } from 'lucide-react';
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/auth/AuthProvider';
import { AUTH_SETTINGS_QUERY_KEY, useAuthOptions, type AuthSettings } from '@/auth/authSettings';
import type { TeamInvitation } from '@/contracts/wire';
import { useSettingsItem } from '@/data/hooks';
import { formatInviteCode, inviteLink, inviteMessage, type AppAuthMethods } from '@/domain/team';
import { Button, Dialog, cn } from '@/ui';
import { inviteExpiryText, inviteJoinDetail, inviteMailto, invitedWho, spelledCode } from './invite';
import { RoleChip, useCopy } from './parts';

/** The company name for the invitation text: the settings item's, else the membership's. */
export function useCompanyName(): string {
  const settings = useSettingsItem();
  const session = useSession();
  const fromSettings = settings?.value.companyName?.trim() ?? '';
  return fromSettings !== '' ? fromSettings : session.companyName;
}

/**
 * The iPhone's sign-in options for "Create an account or sign in (email, Apple or Google)": the server's switches
 * (GoTrue settings), not this console's buttons. Defaults until they are known, as SyncCenter does.
 */
function useAppAuthMethods(): AppAuthMethods | undefined {
  useAuthOptions(); // loads the settings (shared query) and re-renders when they arrive
  const settings = useQueryClient().getQueryData<AuthSettings>(AUTH_SETTINGS_QUERY_KEY);
  return settings ? { email: settings.email, phone: settings.phone, apple: settings.apple, google: settings.google } : undefined;
}

export interface InviteShareViewProps {
  invitation: TeamInvitation;
  /** The invited profile's name, when known. */
  name?: string | null;
}

export function InviteShareView({ invitation, name }: InviteShareViewProps) {
  const companyName = useCompanyName();
  const authMethods = useAppAuthMethods();
  const { copied, copy } = useCopy();
  const code = invitation.code ? formatInviteCode(invitation.code) : '';
  const expiry = inviteExpiryText(invitation);
  const expired = expiry?.expired ?? false;
  const message = useMemo(
    () => inviteMessage(invitation, { companyName, ...(authMethods ? { authMethods } : {}) }),
    [invitation, companyName, authMethods],
  );
  const who = invitedWho(name, invitation);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-4 rounded-card bg-[linear-gradient(135deg,var(--gt-navy),#1F4A9A)] p-5 text-white shadow-card">
        <BadgeCheck aria-hidden className="size-9 shrink-0" />
        <div className="min-w-0">
          <p className="text-lg leading-snug font-bold">{who} is invited</p>
          <p className="mt-1 text-sm text-white/85">{inviteJoinDetail(invitation)}</p>
          <div className="mt-2">
            <RoleChip role={invitation.role} size="xs" />
          </div>
        </div>
      </div>

      <section aria-labelledby="gt-invite-code-title" className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4 shadow-card">
        <h3 id="gt-invite-code-title" className="text-[11px] font-semibold tracking-[0.05em] text-fg-muted uppercase">
          Invite code
        </h3>
        {code !== '' && (
          <p
            className={cn(
              'text-center font-mono text-[28px] leading-tight font-bold tracking-wider select-all sm:text-[30px]',
              expired ? 'text-fg-subtle line-through' : 'text-accent',
            )}
            aria-label={`Invite code ${spelledCode(code)}`}
          >
            {code}
          </p>
        )}
        {expiry && (
          <p
            className={cn(
              'flex items-center justify-center gap-1.5 text-center text-xs',
              expired ? 'text-danger-text' : 'text-fg-muted',
            )}
          >
            {expired ? <TriangleAlert aria-hidden className="size-3.5" /> : <Clock aria-hidden className="size-3.5" />}
            {expiry.text}
          </p>
        )}
        {!expired && (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {code !== '' && (
                <Button
                  icon={copied === 'code' ? <Check /> : <Copy />}
                  onClick={() => void copy(code, 'code')}
                  aria-label={copied === 'code' ? 'Code copied' : 'Copy invite code'}
                >
                  {copied === 'code' ? 'Copied' : 'Copy code'}
                </Button>
              )}
              <Button
                variant="primary"
                icon={copied === 'message' ? <Check /> : <MessageSquareText />}
                onClick={() => void copy(message, 'message')}
                aria-label={copied === 'message' ? 'Message copied' : 'Copy invitation message'}
              >
                {copied === 'message' ? 'Copied' : 'Copy message'}
              </Button>
              {invitation.code && (
                <Button
                  icon={copied === 'link' ? <Check /> : <Link2 />}
                  onClick={() => void copy(inviteLink(invitation.code!), 'link')}
                  aria-label={copied === 'link' ? 'Link copied' : 'Copy join link'}
                >
                  {copied === 'link' ? 'Copied' : 'Copy link'}
                </Button>
              )}
              <a
                href={inviteMailto(invitation, message, companyName)}
                className="inline-flex h-9 items-center justify-center gap-2 rounded-control border border-line-strong bg-surface px-4 text-sm font-semibold text-fg shadow-[0_1px_2px_rgb(16_42_94/0.05)] hover:bg-surface-2 [&_svg]:size-4"
              >
                <Mail aria-hidden />
                Email
              </a>
            </div>
            <p className="text-xs text-fg-muted">
              They install Ground Team, create an account or sign in, tap “Have an invite code?” and enter this code.
              Whoever uses it first joins with this role, so share it only with them. On a computer, the link opens
              this console with the code filled in.
            </p>
          </>
        )}
      </section>

      <details className="rounded-card border border-line bg-surface-2 px-4 py-3 text-sm">
        <summary className="cursor-pointer font-semibold text-fg">Preview the message</summary>
        <pre className="mt-2 font-sans text-[13px] whitespace-pre-wrap text-fg-muted">{message}</pre>
      </details>
    </div>
  );
}

/** InviteSentSheet: the share view in a dialog of its own (an invitation opened from the table). */
export function ShareInvitationDialog({
  invitation,
  name,
  title = 'Invitation',
  onClose,
}: {
  invitation: TeamInvitation | null;
  name?: string | null;
  title?: string;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={invitation !== null}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {invitation && <InviteShareView invitation={invitation} name={name} />}
    </Dialog>
  );
}
